import { useTranslation } from 'react-i18next';
import { GenerationForecastLatestResponse, PvActualDayResponse, ForecastComparisonResponse, GenerationForecastDayResponse } from '@/mocks/generationForecast';

interface Props {
  latest: GenerationForecastLatestResponse | null;
  loading: boolean;
  pvActualData?: PvActualDayResponse | null;
  ecmwfShadowData?: { hourly: Array<{ hour: string; forecastPowerKw: number | null }> } | null;
  comparisonData?: ForecastComparisonResponse | null;
  dayData?: GenerationForecastDayResponse | null;
}

function fmtKwh(v: number | null | undefined, digits = 1): string {
  if (v == null) return '—';
  return Number.isInteger(v) ? `${v}` : v.toFixed(digits);
}

function pickCurrentHourActual(data: PvActualDayResponse | null | undefined): number | null {
  if (!data?.actual?.length) return null;
  const now = new Date();
  const curH = String(now.getHours()).padStart(2, '0') + ':00';
  // 1. Prefer exact current-hour row (live_current sample if available)
  for (const r of data.actual) {
    if ((r as any).hour === curH) {
      const v = (r as any).actualPacKw;
      if (v != null && Number.isFinite(Number(v))) return Number(v);
    }
  }
  // 2. Fall back to most recent past-hour sample that's within the last 1h
  const hOnly = (h: string) => parseInt((h || '00').slice(0, 2), 10);
  const curH_int = now.getHours();
  const candidates = data.actual
    .filter((r: any) => r.actualPacKw != null && Number.isFinite(Number(r.actualPacKw)))
    .filter((r: any) => {
      const hr = hOnly(r.hour);
      return hr === curH_int - 1 || hr === curH_int; // within current or prev hour
    });
  if (candidates.length) {
    const latest = candidates[candidates.length - 1];
    return Number((latest as any).actualPacKw);
  }
  return null;
}

function pickCurrentEcmwf(
  data: { hourly: Array<{ hour: string; forecastPowerKw: number | null }> } | null | undefined,
): number | null {
  if (!data?.hourly?.length) return null;
  const now = new Date();
  const currentHour = `${String(now.getHours()).padStart(2, '0')}:00`;
  // Find current hour exact, else fallback to the latest hour ≤ currentHour
  const exact = data.hourly.find((r) => r.hour === currentHour);
  if (exact && exact.forecastPowerKw != null) return Number(exact.forecastPowerKw);
  const past = data.hourly.filter((r) => r.hour <= currentHour && r.forecastPowerKw != null);
  if (past.length) return Number(past[past.length - 1].forecastPowerKw);
  return null;
}

// Current-hour Base forecast (v0.2.2) value, kW — pairs with the ECMWF current tile.
function pickCurrentBase(data: ForecastComparisonResponse | null | undefined): number | null {
  const pts = (data as any)?.points;
  if (!Array.isArray(pts) || !pts.length) return null;
  const now = new Date();
  const curH = `${String(now.getHours()).padStart(2, '0')}:00`;
  const hourOf = (p: any) => {
    const raw = String(p.hour ?? p.forecastHour ?? '');
    const m = raw.match(/(\d{2}:\d{2})/);
    return m ? m[1] : raw.slice(0, 5);
  };
  const valOf = (p: any) => {
    const v = p.trueBaseForecastKw ?? p.baseForecastKw;
    return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
  };
  const exact = pts.find((p: any) => hourOf(p) === curH && valOf(p) != null);
  if (exact) return valOf(exact);
  const past = pts.filter((p: any) => hourOf(p) <= curH && valOf(p) != null);
  if (past.length) return valOf(past[past.length - 1]);
  return null;
}

export default function ForecastKpi({ latest, loading, pvActualData, ecmwfShadowData, comparisonData, dayData }: Props) {
  const { t } = useTranslation();
  const summary = latest?.summary;
  const actualNow = pickCurrentHourActual(pvActualData);
  const ecmwfNow = pickCurrentEcmwf(ecmwfShadowData);
  const baseNow = pickCurrentBase(comparisonData);

  // Use comparisonData.summary (24-hour day total) as primary source.
  // Fall back to latest.daily today row when comparisonData not loaded yet.
  // Avoid latest.summary.totalForecastEnergyKwh — that's a 5-day total, not today.
  const todayIso = new Date().toISOString().slice(0, 10);
  const todayDaily = (latest as any)?.daily?.find((d: any) => d?.day === todayIso);
  const cmpSummary = (comparisonData as any)?.summary;
  // Σ Base v0.2.2 з ТОГО Ж джерела, що й таблиця «Погодинний прогноз vs факт»
  // (dayData.hourly.forecastEnergyKwh), а НЕ comparisonData.baseForecastKwhApprox —
  // бо для ECMWF-routed активів (Табаки) те поле = ECMWF, не база (+50%).
  const _dayHourly = (dayData as any)?.hourly as any[] | undefined;
  const _baseDayKwh = _dayHourly && _dayHourly.length
    ? _dayHourly.reduce((s, h) => s + (Number(h?.forecastEnergyKwh) || 0), 0)
    : null;
  const todayTotalKwh = _baseDayKwh
    ?? todayDaily?.forecastEnergyKwh
    ?? cmpSummary?.baseForecastKwhApprox
    ?? null;

  const items = [
    {
      label: t('fc_kpi_total_energy'),
      value: fmtKwh(todayTotalKwh),
      unit: 'kWh',
      icon: 'ri-bar-chart-2-line',
      color: '#D12229',
    },
    {
      label: t('fc_kpi_actual_pv'),
      value: fmtKwh(actualNow, 1),
      unit: 'kW',
      icon: 'ri-sun-line',
      color: '#22C55E',
    },
    {
      label: `Base Forecast (${t('fc_current')})`,
      value: fmtKwh(baseNow, 1),
      unit: 'kW',
      icon: 'ri-line-chart-line',
      color: '#F59E0B',
    },
    {
      label: `ECMWF_ifs025 (${t('fc_current')})`,
      value: fmtKwh(ecmwfNow, 1),
      unit: 'kW',
      icon: 'ri-cloud-line',
      color: '#06B6D4',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((kpi) => (
        <div
          key={kpi.label}
          className="rounded-xl p-4 flex items-center gap-4"
          style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${kpi.color}18` }}
          >
            <i className={`${kpi.icon} text-lg`} style={{ color: kpi.color }}></i>
          </div>
          <div className="min-w-0">
            <div className="text-xs mb-0.5" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>
              {kpi.label}
            </div>
            <div className="flex items-baseline gap-1">
              {loading ? (
                <span className="text-xl font-bold text-white">—</span>
              ) : (
                <span className="font-bold text-white text-xl" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {kpi.value}
                </span>
              )}
              {kpi.unit && (
                <span className="text-xs" style={{ color: '#6B6B70' }}>{kpi.unit}</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
